package devxertech.com.pages.blogs

import androidx.compose.runtime.NoLiveLiterals
import kotlinx.browser.window
import kotlinx.browser.document
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.JsonArray
import kotlinx.serialization.json.JsonElement
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.JsonNull
import kotlinx.serialization.json.JsonPrimitive
import kotlinx.serialization.json.contentOrNull
import kotlinx.serialization.json.jsonArray
import kotlinx.serialization.json.jsonObject
import kotlinx.serialization.encodeToString
import kotlin.js.Promise
import kotlin.js.unsafeCast

private const val SANITY_PROJECT_ID = "e2zekld1"
private const val SANITY_DATASET = "production"
private const val SANITY_API_VERSION = "2025-02-19"
private const val SANITY_QUERY_ENDPOINT =
    "https://$SANITY_PROJECT_ID.apicdn.sanity.io/v$SANITY_API_VERSION/data/query/$SANITY_DATASET"
private const val BLOG_CACHE_TTL_MS = 60_000.0

private val sanityJson = Json { ignoreUnknownKeys = true }

// The page module lives for the browser tab's lifetime, so successful responses
// are reused across client-side navigation and Back / Forward remounts.
private var cachedPublishedPosts: List<SanityBlogPost>? = null
private var cachedPublishedPostsAt = 0.0
private var pendingPublishedPosts: Promise<List<SanityBlogPost>>? = null
private val cachedPublishedPostsBySlug = mutableMapOf<String, SanityBlogPost>()
private val cachedPublishedPostTimes = mutableMapOf<String, Double>()
private val pendingPublishedPostsBySlug = mutableMapOf<String, Promise<SanityBlogPost?>>()

private const val POST_PROJECTION = """
{
  _id,
  title,
  "slug": slug.current,
  excerpt,
  publishedAt,
  _updatedAt,
  seoTitle,
  seoDescription,
  "authorName": author->name,
  "categories": categories[]->title,
  "coverImage": {
    "url": coalesce(coverImage.asset->url, coverImage.sourcePath),
    "alt": coverImage.alt
  },
  "heroImage": {
    "url": coalesce(heroImage.asset->url, heroImage.sourcePath),
    "alt": heroImage.alt
  },
  "articleSections": articleSections[]{
    ...,
    "image": image{ "url": coalesce(asset->url, sourcePath), alt },
    points[]{..., "_key": _key},
    categories[]{..., "_key": _key}
  },
  "body": body[]{
    ...,
    "url": asset->url
  }
}
"""

// Cards and filters do not need every article's body, hero and section content.
private const val LISTING_PROJECTION = """
{
  _id, title, "slug": slug.current, excerpt, publishedAt, _updatedAt,
  "authorName": author->name,
  "categories": categories[]->title,
  "coverImage": {
    "url": coalesce(coverImage.asset->url, coverImage.sourcePath),
    "alt": coverImage.alt
  }
}
"""

private val PUBLISHED_POSTS_QUERY = """
*[
  _type == "post" &&
  defined(slug.current) &&
  defined(publishedAt) &&
  dateTime(publishedAt) <= dateTime(now())
] | order(publishedAt desc) $LISTING_PROJECTION
""".trimIndent()

private val PUBLISHED_POST_BY_SLUG_QUERY = """
*[
  _type == "post" &&
  slug.current == ${'$'}slug &&
  defined(publishedAt) &&
  dateTime(publishedAt) <= dateTime(now())
][0] $POST_PROJECTION
""".trimIndent()

/** Fetches only the published perspective. No credentials or tokens are sent. */
fun fetchPublishedPosts(): Promise<List<SanityBlogPost>> {
    cachedPublishedPosts?.takeIf { isCacheFresh(cachedPublishedPostsAt) }?.let { return Promise.resolve(it) }
    pendingPublishedPosts?.let { return it }
    val request = fetchSanityQuery(PUBLISHED_POSTS_QUERY).then { response ->
        decodePosts(response).also { posts ->
            cachedPublishedPosts = posts
            cachedPublishedPostsAt = currentTimeMillis()
            saveBlogSnapshot("listing", response)
        }
    }
    pendingPublishedPosts = request
    request.then({ pendingPublishedPosts = null; Unit }, { pendingPublishedPosts = null; Unit })
    return request
}

/** Returns null for an unknown or unpublished slug. */
@NoLiveLiterals
fun fetchPublishedPost(slug: String): Promise<SanityBlogPost?> {
    cachedPublishedPostsBySlug[slug]
        ?.takeIf { isCacheFresh(cachedPublishedPostTimes[slug] ?: 0.0) }
        ?.let { return Promise.resolve(it) }
    cachedPublishedPostsBySlug.remove(slug)
    cachedPublishedPostTimes.remove(slug)
    pendingPublishedPostsBySlug[slug]?.let { return it }
    val params: dynamic = js("new URLSearchParams()")
    params.set("query", PUBLISHED_POST_BY_SLUG_QUERY)
    params.set("\$slug", sanityJson.encodeToString(slug))
    params.set("perspective", "published")
    val request = fetchSanityUrl(params.toString() as String).then { response ->
        decodePost(response).also { if (it != null) saveBlogSnapshot("article", response, slug) }
    }
    pendingPublishedPostsBySlug[slug] = request
    request.then({ post ->
        pendingPublishedPostsBySlug.remove(slug)
        if (post != null) {
            cachedPublishedPostsBySlug[slug] = post
            cachedPublishedPostTimes[slug] = currentTimeMillis()
        }
        post
    }, {
        pendingPublishedPostsBySlug.remove(slug)
        Unit
    })
    return request
}

private fun isCacheFresh(cachedAt: Double): Boolean =
    currentTimeMillis() - cachedAt < BLOG_CACHE_TTL_MS

@NoLiveLiterals
private fun currentTimeMillis(): Double = js("Date.now()") as Double

/** Starts the normal published-only request early from a visible card. */
fun prefetchPublishedPost(slug: String) {
    fetchPublishedPost(slug).catch { null }
}

/** Published data in the head survives Kobweb clearing the exported root. */
internal fun initialPublishedPosts(): List<SanityBlogPost>? {
    cachedPublishedPosts?.let { return it }
    return readBlogSnapshot("listing")?.let { response ->
        runCatching { decodePosts(response) }.getOrNull()?.also { cachedPublishedPosts = it }
    }
}

internal fun initialPublishedPost(slug: String): SanityBlogPost? {
    cachedPublishedPostsBySlug[slug]?.let { return it }
    return readBlogSnapshot("article", slug)?.let { response ->
        runCatching { decodePost(response) }.getOrNull()
            ?.takeIf { it.slug == slug }
            ?.also { cachedPublishedPostsBySlug[slug] = it }
    }
}

private fun readBlogSnapshot(kind: String, slug: String = ""): String? {
    val element = document.getElementById("devxer-blog-$kind-data") ?: return null
    if (element.getAttribute("data-blog-slug").orEmpty() != slug) return null
    return element.textContent?.takeIf(String::isNotBlank)
}

private fun saveBlogSnapshot(kind: String, response: String, slug: String = "") {
    val id = "devxer-blog-$kind-data"
    val element = document.getElementById(id) ?: document.createElement("script").also {
        it.id = id
        it.setAttribute("type", "application/json")
        document.head?.appendChild(it)
    }
    element.setAttribute("data-blog-slug", slug)
    // HTML snapshots must not interpret CMS text as an end-script delimiter.
    element.textContent = response.replace("<", "\\u003c")
}

@NoLiveLiterals
private fun fetchSanityQuery(query: String): Promise<String> {
    val params: dynamic = js("new URLSearchParams()")
    params.set("query", query)
    params.set("perspective", "published")
    return fetchSanityUrl(params.toString() as String)
}

private fun fetchSanityUrl(search: String): Promise<String> =
    window.fetch("$SANITY_QUERY_ENDPOINT?$search").then { response ->
        if (!response.ok) {
            throw IllegalStateException("Sanity returned HTTP ${response.status}")
        }
        response.text()
    }.unsafeCast<Promise<String>>()

private fun decodePosts(response: String): List<SanityBlogPost> {
    val envelope = parseResponse(response)
    check(envelope["error"] == null) { "Sanity returned an API error" }
    val result = envelope["result"] as? JsonArray ?: error("Sanity listing result is not an array")
    return result.map { it.toSanityBlogPost() ?: error("Sanity returned an invalid published post") }
}

private fun decodePost(response: String): SanityBlogPost? {
    val envelope = parseResponse(response)
    check(envelope["error"] == null) { "Sanity returned an API error" }
    val result = envelope["result"] ?: error("Sanity article response is missing its result")
    if (result == JsonNull) return null
    return result.toSanityBlogPost() ?: error("Sanity returned an invalid article")
}

private fun parseResponse(response: String): JsonObject =
    sanityJson.parseToJsonElement(response).jsonObject

private fun JsonElement.toSanityBlogPost(): SanityBlogPost? {
    val post = this as? JsonObject ?: return null
    val slug = post.string("slug")?.takeIf(String::isNotBlank) ?: return null
    val cover = post["coverImage"].asObject()
    val coverUrl = cover?.string("url").orEmpty()
    val coverAlt = cover?.string("alt").orEmpty()
    val hero = post["heroImage"].asObject()

    return SanityBlogPost(
        id = post.string("_id").orEmpty(),
        title = post.string("title").orEmpty(),
        slug = slug,
        excerpt = post.string("excerpt").orEmpty(),
        publishedAt = post.string("publishedAt").orEmpty(),
        updatedAt = post.string("_updatedAt"),
        authorName = post.string("authorName").orEmpty(),
        categories = post["categories"].asArray().mapNotNull { it.asString() },
        coverImageUrl = coverUrl,
        coverImageAlt = coverAlt,
        heroImageUrl = hero?.string("url").orEmpty(),
        heroImageAlt = hero?.string("alt").orEmpty(),
        seoTitle = post.string("seoTitle").orEmpty(),
        seoDescription = post.string("seoDescription").orEmpty(),
        body = post["body"].asArray().map { it.toPortableBlock() ?: error("Unsupported or invalid article body block") },
        articleSections = post["articleSections"].asArray().map { it.toArticleSection() ?: error("Invalid article section") },
    )
}

private fun JsonElement.toArticleSection(): ArticleSection? {
    val section = this as? JsonObject ?: return null
    val image = section["image"].asObject()
    return ArticleSection(
        type = section.string("_type").orEmpty(),
        heading = section.string("heading").orEmpty(),
        text = section.string("text").orEmpty(),
        lead = section.string("lead").orEmpty(),
        highlighted = section.string("highlighted").orEmpty(),
        trailing = section.string("trailing").orEmpty(),
        imageUrl = image?.string("url").orEmpty(),
        imageAlt = image?.string("alt").orEmpty(),
        points = section["points"].asArray().mapNotNull { value ->
            val point = value.asObject() ?: return@mapNotNull null
            NumberedArticlePoint(
                number = point.string("number").orEmpty(),
                title = point.string("title").orEmpty(),
                description = point.string("description").orEmpty(),
            )
        },
        categories = section["categories"].asArray().mapNotNull { value ->
            val category = value.asObject() ?: return@mapNotNull null
            BusinessUseCategory(
                title = category.string("title").orEmpty(),
                points = category["points"].asArray().mapNotNull { it.asString() },
            )
        },
    )
}

private fun JsonElement.toPortableBlock(): PortableBlock? {
    val block = this as? JsonObject ?: return null
    val type = block.string("_type") ?: return null
    if (type == "youtubeVideo") {
        return PortableBlock(key = block.string("_key").orEmpty(), type = type,
            youtubeUrl = block.string("youtubeUrl").orEmpty(), videoTitle = block.string("videoTitle").orEmpty(),
            caption = block.string("caption").orEmpty())
    }
    if (type == "image") {
        return PortableBlock(
            key = block.string("_key").orEmpty(),
            type = type,
            imageUrl = block.string("url").orEmpty(),
            imageAlt = block.string("alt").orEmpty(),
            caption = block.string("caption").orEmpty(),
        )
    }
    if (type != "block") return null

    val markDefs = block["markDefs"].asArray().mapNotNull { value ->
        val definition = value.asObject() ?: return@mapNotNull null
        val key = definition.string("_key") ?: return@mapNotNull null
        val href = definition.string("href") ?: return@mapNotNull null
        key to href
    }.toMap()

    val spans = block["children"].asArray().mapNotNull { value ->
        val span = value.asObject() ?: return@mapNotNull null
        if (span.string("_type") != "span") return@mapNotNull null
        PortableTextSpan(
            text = span.string("text").orEmpty(),
            marks = span["marks"].asArray().mapNotNull { it.asString() },
        )
    }

    return PortableBlock(
        key = block.string("_key").orEmpty(),
        type = type,
        style = block.string("style").orEmpty(),
        listItem = block.string("listItem"),
        spans = spans,
        markDefs = markDefs,
    )
}

private fun JsonObject.string(name: String): String? = this[name]?.asString()

private fun JsonElement?.asObject(): JsonObject? = this as? JsonObject

private fun JsonElement?.asArray(): List<JsonElement> =
    (this as? JsonArray)?.jsonArray ?: emptyList()

private fun JsonElement?.asString(): String? =
    (this as? JsonPrimitive)?.contentOrNull

data class SanityBlogPost(
    val id: String,
    val title: String,
    val slug: String,
    val excerpt: String,
    val publishedAt: String,
    val updatedAt: String?,
    val authorName: String,
    val categories: List<String>,
    val coverImageUrl: String,
    val coverImageAlt: String,
    val heroImageUrl: String,
    val heroImageAlt: String,
    val seoTitle: String,
    val seoDescription: String,
    val body: List<PortableBlock>,
    val articleSections: List<ArticleSection>,
)

data class ArticleSection(
    val type: String,
    val heading: String = "",
    val text: String = "",
    val lead: String = "",
    val highlighted: String = "",
    val trailing: String = "",
    val imageUrl: String = "",
    val imageAlt: String = "",
    val points: List<NumberedArticlePoint> = emptyList(),
    val categories: List<BusinessUseCategory> = emptyList(),
)

data class NumberedArticlePoint(val number: String, val title: String, val description: String)

data class BusinessUseCategory(val title: String, val points: List<String>)

data class PortableTextSpan(
    val text: String,
    val marks: List<String>,
)

data class PortableBlock(
    val key: String,
    val type: String,
    val style: String = "normal",
    val listItem: String? = null,
    val spans: List<PortableTextSpan> = emptyList(),
    val markDefs: Map<String, String> = emptyMap(),
    val imageUrl: String = "",
    val imageAlt: String = "",
    val caption: String = "",
    val youtubeUrl: String = "",
    val videoTitle: String = "",
)
